import { cloneDeep, values, size, without } from 'lodash';
import moment from 'moment-timezone';
import {
  ACTIVE,
  APPROVED,
  AWAITING_SELECTION,
  CANCELLED,
  CLOSED,
  DISPUTED,
  EXPIRED,
  EXPIRED_AND_NOT_DISMISSED,
  OPEN,
  PENDING_CREDIT_APPROVAL,
  TIMESHEET_AWAITING_APPROVAL,
  UPCOMING,
} from './graphql/constants';

import { TRANSFER_ORGANISATION_JOBS } from '../PolicyProvider';

export const SELECT_WEPLOYEES = 'SELECT_WEPLOYEES';
export const DISMISS = 'DISMISS';

const primaryActionText = {
  [SELECT_WEPLOYEES]: 'Select Weployees',
  [DISMISS]: 'Dismiss',
};

const jobStatuses = {
  [EXPIRED_AND_NOT_DISMISSED]: 'Sorry, no weployees matched',
  [OPEN]: 'Matching job with Weployees',
  [PENDING_CREDIT_APPROVAL]: 'Awaiting credit approval',
  [AWAITING_SELECTION]: 'Weployees found, time to select!',
};

const statusSubTexts = {
  [EXPIRED_AND_NOT_DISMISSED]: 'Selection ended, please try again',
  [OPEN]: 'Hold tight, we\'ll notify you soon',
  [PENDING_CREDIT_APPROVAL]: 'Hold tight, we\'ll be in touch',
  [AWAITING_SELECTION]: null,
};

const clickableJob = job => (
  ![PENDING_CREDIT_APPROVAL].includes(job.status)
);

const activePosition = position => (
  [ACTIVE, TIMESHEET_AWAITING_APPROVAL].includes(position.status)
);

const pastJob = job => (
  [CLOSED, EXPIRED].includes(job.status)
);

const collectStatusMessages = (positions) => {
  const extensionsRequestedCount = positions.filter(position => position.hasPendingExtensionRequests).length;
  const finishingSoonCount = positions.filter(position => (position.isFinishingSoon && activePosition(position))).length;
  const timesheetsToApproveCount = positions.filter(position => position.hasTimesheetAwaitingApproval).length;

  let extensionsRequestedMessage = '';
  let weployeesFinishingSoonMessage = '';
  let timesheetsToApproveMessage = '';

  if (extensionsRequestedCount > 0) {
    extensionsRequestedMessage = extensionsRequestedCount > 1
      ? `${extensionsRequestedCount} extensions requested`
      : `${extensionsRequestedCount} extension requested`;
  }

  if (finishingSoonCount > 0) {
    weployeesFinishingSoonMessage = finishingSoonCount > 1
      ? `${finishingSoonCount} Weployees finishing soon`
      : `${finishingSoonCount} Weployee finishing soon`;
  }

  if (timesheetsToApproveCount > 0) {
    timesheetsToApproveMessage = timesheetsToApproveCount > 1
      ? `${timesheetsToApproveCount} timesheets to approve`
      : `${timesheetsToApproveCount} timesheet to approve`;
  }

  return without([extensionsRequestedMessage, weployeesFinishingSoonMessage, timesheetsToApproveMessage], '');
};

const jobStatus = (status, numberOfWeployees) => {
  if (status === AWAITING_SELECTION) {
    return numberOfWeployees > 1 ? 'Weployees found, time to select!' : 'Weployee found, time to select!';
  }

  return jobStatuses[status];
};

const isSelectJob = (job) => {
  const { instant } = job;
  return !instant;
};

export const expireJob = (jobs, jobId) => {
  const updatedJobs = cloneDeep(jobs);
  updatedJobs[jobId].status = EXPIRED_AND_NOT_DISMISSED;

  return updatedJobs;
};

export const jobsExcluding = (jobs, jobId) => {
  const updatedJobs = cloneDeep(jobs);
  delete updatedJobs[jobId];

  return updatedJobs;
};

const isOverflowMenuVisible = (job, actions) => (
  job.belongsToCurrentUser || actions.includes(TRANSFER_ORGANISATION_JOBS)
);

export const openJobs = (jobs, actions) =>
  values(jobs)
    .filter(job => ([EXPIRED_AND_NOT_DISMISSED, OPEN, PENDING_CREDIT_APPROVAL, AWAITING_SELECTION].includes(job.status)))
    .map((job) => {
      const {
        belongsToCurrentUser,
        id,
        expiryTime,
        instant,
        isTransferrable,
        owner,
        numberOfPositions,
        latestPositionFinishTime,
        location: { city, state },
        startTime,
        finishTime,
        status,
        title,
      } = job;

      const primaryButtonAction = (statusToMap, isDirectJob) => {
        switch (statusToMap) {
          case AWAITING_SELECTION:
            return SELECT_WEPLOYEES;
          case EXPIRED_AND_NOT_DISMISSED:
            return DISMISS;
          case OPEN: {
            if (!isDirectJob) { return SELECT_WEPLOYEES; }
            return null;
          }
          default:
            return null;
        }
      };

      const primaryButtonActionType = primaryButtonAction(status, instant);
      return {
        avatars: [],
        belongsToCurrentUser,
        city,
        expiryTime,
        finishTime: latestPositionFinishTime || finishTime,
        highlighted: status === AWAITING_SELECTION,
        id,
        instant,
        isClickable: clickableJob(job),
        isOverflowMenuVisible: isOverflowMenuVisible(job, actions),
        isTransferrable,
        loaderVisible: status === OPEN,
        numberOfWeployees: numberOfPositions,
        owner,
        primaryButtonActionType,
        primaryButtonText: primaryActionText[primaryButtonActionType] || null,
        startTime,
        state,
        status: jobStatus(status, numberOfPositions),
        statusSubText: (status === EXPIRED_AND_NOT_DISMISSED || !isSelectJob(job)) ? statusSubTexts[status] : null,
        title,
        timerVisible: isSelectJob(job) && [OPEN, AWAITING_SELECTION].includes(status),
      };
    });


export const activeJobs = (jobs, positions, weployees, actions) => (
  values(jobs)
    .filter(job => job.status === ACTIVE)
    .map((job) => {
      const {
        belongsToCurrentUser,
        id,
        instant,
        isTransferrable,
        latestPositionFinishTime,
        owner,
        startTime,
        title,
        positions: positionIds,
      } = job;

      const weployeesList = positionIds.map((positionId) => {
        const { weployee: weployeeId } = positions[positionId];
        return weployeeId ? {
          profileImageUrl: weployees[weployeeId].profileImageUrl,
          status: positions[positionId].status,
        } : null;
      }).filter(Boolean);

      const avatars = weployeesList
        .filter(weployee => [ACTIVE, TIMESHEET_AWAITING_APPROVAL].includes(weployee.status))
        .map(weployee => (
          {
            url: (weployee.profileImageUrl),
          }
        ));

      return {
        avatars,
        belongsToCurrentUser,
        city: job.location.city,
        finishTime: latestPositionFinishTime,
        id,
        instant,
        isClickable: clickableJob(job),
        isOverflowMenuVisible: isOverflowMenuVisible(job, actions),
        isTransferrable,
        numberOfWeployees: size(avatars),
        owner,
        state: job.location.state,
        startTime,
        statusMessages: collectStatusMessages(positionIds.map(positionId => positions[positionId])),
        title,
        weployees: weployeesList,
      };
    }));

export const upcomingJobs = (jobs, positions, weployees, actions) => (
  values(jobs)
    .filter(job => job.status === UPCOMING)
    .map((job) => {
      const {
        belongsToCurrentUser,
        id,
        instant,
        isTransferrable,
        latestPositionFinishTime,
        owner,
        startTime,
        title,
        positions: positionIds,
      } = job;

      const weployeesList = positionIds.map((positionId) => {
        const { weployee: weployeeId } = positions[positionId];

        return weployeeId ? {
          profileImageUrl: weployees[weployeeId].profileImageUrl,
          status: positions[positionId].status,
        } : null;
      }).filter(Boolean);

      const avatars = weployeesList
        .filter(weployee => [ACTIVE].includes(weployee.status))
        .map(weployee => (
          {
            url: (weployee.profileImageUrl),
          }
        ));

      return {
        avatars,
        belongsToCurrentUser,
        city: job.location.city,
        finishTime: latestPositionFinishTime,
        id,
        instant,
        isClickable: clickableJob(job),
        isOverflowMenuVisible: isOverflowMenuVisible(job, actions),
        isTransferrable,
        numberOfWeployees: size(avatars),
        owner,
        state: job.location.state,
        startTime,
        statusMessages: collectStatusMessages(positionIds.map(positionId => positions[positionId])),
        title,
        weployees: weployeesList,
      };
    })
);

export const pastJobs = (jobs, positions, weployees, ownedByCurrentUser, actions) => (
  values(jobs)
    .filter(job => (ownedByCurrentUser ? pastJob(job) && job.belongsToCurrentUser : pastJob(job)))
    .map((job) => {
      const {
        belongsToCurrentUser,
        id,
        instant,
        isTransferrable,
        latestPositionFinishTime,
        owner,
        startTime,
        status,
        title,
        positions: positionIds,
      } = job;

      const weployeesList = positionIds.map((positionId) => {
        const { weployee: weployeeId } = positions[positionId];

        return weployeeId ? {
          profileImageUrl: weployees[weployeeId].profileImageUrl,
          status: positions[positionId].status,
        } : null;
      }).filter(Boolean);

      const avatars = weployeesList
        .filter(weployee => [APPROVED, DISPUTED, CANCELLED].includes(weployee.status))
        .map(weployee => (
          {
            url: (weployee.profileImageUrl),
          }
        ));
      return {
        avatars,
        belongsToCurrentUser,
        city: job.location.city,
        finishTime: latestPositionFinishTime,
        id,
        instant,
        isClickable: clickableJob(job),
        isOverflowMenuVisible: isOverflowMenuVisible(job, actions),
        isTransferrable,
        numberOfWeployees: size(avatars),
        owner,
        state: job.location.state,
        statusMessages: (status === EXPIRED) ? ['Closed'] : null,
        startTime,
        title,
        weployees: weployeesList,
      };
    })
    .sort((jobA, jobB) => moment(jobB.startTime) - moment(jobA.startTime))
);
